import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { quotaSchema, tokenSchema, type Quota, type TokenResponse } from '../../../shared/api/contracts/styleTransferSchemas';
import { createTTLJSONStorage } from '../../../shared/storage/createTTLJSONStorage';

export type QuotaStatus = 'idle' | 'ready' | 'error';

export interface SessionState {
  token: TokenResponse | null;
  quota: Quota | null;
  isAuthenticated: boolean;
  quotaStatus: QuotaStatus;
  setToken: (token: TokenResponse) => void;
  updateQuota: (quota: Quota) => void;
  setQuotaStatus: (status: QuotaStatus) => void;
  clearSession: () => void;
  logout: () => void;
}

const SESSION_PERSIST_KEY = 'photo-studio-session';
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const normalizeToken = (token: TokenResponse): TokenResponse => tokenSchema.parse(token);
const normalizeQuota = (quota: Quota): Quota => quotaSchema.parse(quota);

type SessionPersistedState = Pick<SessionState, 'token' | 'quota' | 'isAuthenticated'>;

export const useSessionStore = create<SessionState>()(
  persist<SessionState, [], SessionPersistedState>(
    (set) => ({
      token: null,
      quota: null,
      isAuthenticated: false,
      quotaStatus: 'idle',
      logout: () =>
        set(() => ({
          token: null,
          quota: null,
          isAuthenticated: false,
          quotaStatus: 'idle'
        })),
      setToken: (token) =>
        set(() => ({
          token: normalizeToken(token),
          quota: normalizeQuota(token.quota),
          isAuthenticated: token.tokenType === 'user',
          quotaStatus: 'ready'
        })),
      updateQuota: (quota) =>
        set((state) => ({
          quota: normalizeQuota(quota),
          token: state.token ? { ...state.token, quota: normalizeQuota(quota) } : state.token,
          quotaStatus: 'ready'
        })),
      setQuotaStatus: (status) =>
        set((state) => ({
          quotaStatus: status,
          token: state.token ? { ...state.token } : state.token
        })),
      clearSession: () =>
        set(() => ({
          token: null,
          quota: null,
          isAuthenticated: false,
          quotaStatus: 'idle'
        }))
    }),
    {
      name: SESSION_PERSIST_KEY,
      storage: createTTLJSONStorage<SessionPersistedState>(ONE_WEEK_MS),
      partialize: (state) => ({
        token: state.token,
        quota: state.quota,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);

export const selectToken = (state: SessionState) => state.token;
export const selectQuota = (state: SessionState) => state.quota;
export const selectIsAuthenticated = (state: SessionState) => state.isAuthenticated;
export const selectQuotaStatus = (state: SessionState) => state.quotaStatus;
